import { Button, ButtonGroup, Classes, Intent, NonIdealState } from "@blueprintjs/core";
import classNames from "classnames";
import { action, observable, reaction } from "mobx";
import { observer } from "mobx-react";
import React from "react";

import { PerformanceSceneMessage } from "../../chat";
import {
    Configuration,
    ErrorBoundary,
    getDefaultChatRoomState,
    getDefaultLocalUserState,
    getDefaultMediaDevicesState,
    getDefaultPerformanceState,
    getDefaultRoomState,
} from "../../common";
import { MediaDevicesManager, PublicMessageManager } from "../data";
import JitsiConferenceManager from "../data/JitsiConferenceManager";
import { JitsiManagerChildProps } from "../data/JitsiManager";
import { ChatRoom } from "../ui";
import FloatingWindow from "./FloatingWindow";
import PerformanceMusicPlayer from "./PerformanceMusicPlayer";
import styles from "./PerformanceStage.module.css";
import PerformerGrid from "./PerformerGrid";
import PerformerMessagesManager from "./PerformerMessagesManager";
import StageBackground from "./StageBackground";

export interface PerformanceStageProps extends JitsiManagerChildProps {
    performerId?: number;
}

@observer
export default class PerformanceStage extends React.PureComponent<PerformanceStageProps> {
    public static displayName = "PerformanceStage";

    @observable public roomState = getDefaultRoomState();
    @observable public localUserState = getDefaultLocalUserState(this.props.room.myUserId(), {
        performerId: this.props.performerId,
    });
    @observable public mediaDevicesState = getDefaultMediaDevicesState();
    @observable public chatRoomState = getDefaultChatRoomState();
    @observable public performanceState = getDefaultPerformanceState();

    private advanceTimeoutId?: number;

    public async componentDidMount() {
        if (Configuration.AUTO_JOIN_ROOM) {
            this.joinRoom();
        }

        reaction(
            () => this.performanceState.scene,
            (currentScene) => {
                window.clearTimeout(this.advanceTimeoutId);
                const currentPrompt = this.performanceState.scenePrompts.get(currentScene);
                const advanceTimeout = currentPrompt?.advanceWithTimeout;
                if (advanceTimeout !== undefined) {
                    this.advanceTimeoutId = window.setTimeout(this.advanceScene, advanceTimeout);
                }

                if (currentPrompt?.music === null) {
                    this.performanceState.sceneDuos = undefined;
                }
            }
        );
    }

    public componentWillUnmount() {
        window.clearTimeout(this.advanceTimeoutId);
    }

    public render() {
        const { jitsi, room, performerId } = this.props;
        const { scene, scenePrompts } = this.performanceState;

        return (
            <div className={classNames(styles.container, `bg-${scenePrompts.get(scene)?.stageBackground}`)}>
                <StageBackground performanceState={this.performanceState} />
                <JitsiConferenceManager
                    {...this.props}
                    roomState={this.roomState}
                    localUser={this.localUserState}
                    performanceState={this.performanceState}
                />
                <PerformanceMusicPlayer
                    performanceState={this.performanceState}
                    chatUIState={this.chatRoomState.public}
                />
                {performerId !== undefined && (
                    <MediaDevicesManager {...this.props} mediaDevicesState={this.mediaDevicesState} />
                )}
                {this.roomState.roomJoined === "joined" && (
                    <div className={styles.sidebar}>{this.renderChatView()}</div>
                )}
                <div className={styles.remoteViewContainer}>
                    {this.roomState.roomJoined === "joined" ? (
                        <PerformerGrid
                            {...this.props}
                            roomState={this.roomState}
                            performance={this.performanceState}
                            mediaDevices={this.mediaDevicesState}
                            localUser={this.localUserState}
                        />
                    ) : (
                        <NonIdealState
                            title={`Welcome`}
                            icon="globe-network"
                            description={
                                <Button
                                    text="Enter the performance"
                                    large={true}
                                    intent={Intent.SUCCESS}
                                    loading={this.roomState.roomJoined === "joining"}
                                    onClick={this.joinRoom}
                                />
                            }
                        />
                    )}
                    <PerformerMessagesManager
                        jitsi={jitsi}
                        room={room}
                        performance={this.performanceState}
                        localUser={this.localUserState}
                    />
                </div>
            </div>
        );
    }

    private renderChatView() {
        const { jitsi, room, performerId } = this.props;

        return (
            <div className={styles.chatViewContainer}>
                <FloatingWindow className={styles.chatWindow} title="👾 Cyberdance chat">
                    {performerId && this.renderSceneMovementButtons()}
                    <ErrorBoundary>
                        <ChatRoom
                            jitsi={jitsi}
                            room={room}
                            uiState={this.chatRoomState.public}
                            inputProps={{
                                disabled: this.localUserState.displayName === undefined,
                            }}
                            isSelfViewHidden={true}
                            localUser={this.localUserState}
                            useDarkTheme={false}
                            performanceStageTheme={true}
                            roomTopic={this.performanceState.scenePrompts.get(this.performanceState.scene)?.prompt}
                            mentionUserIds={this.roomState.videoLayout.map((l) => l.userId)}
                            // showMessageColors={performerId !== undefined}
                        />
                        <PublicMessageManager jitsi={jitsi} room={room} uiState={this.chatRoomState.public} />
                    </ErrorBoundary>
                </FloatingWindow>
            </div>
        );
    }

    private renderSceneMovementButtons() {
        const { scene, scenePrompts } = this.performanceState;

        return (
            <ButtonGroup className={classNames(Classes.DARK, styles.sceneMovementButtons)}>
                <Button
                    className={styles.revertSceneButton}
                    icon="arrow-left"
                    small={true}
                    outlined={true}
                    disabled={scene === 0}
                    onClick={this.revertScene}
                />
                <Button
                    className={styles.advanceSceneButton}
                    icon="arrow-right"
                    small={true}
                    outlined={true}
                    disabled={scene === scenePrompts.size}
                    onClick={this.advanceScene}
                />
            </ButtonGroup>
        );
    }

    @action public advanceScene = () => {
        window.clearTimeout(this.advanceTimeoutId);
        this.performanceState.scene += 1;
        // if (
        //     this.performanceState.scenePrompts.get(this.performanceState.scene)
        //         ?.advanceWithMentions === "all"
        // ) {
        //     this.performanceState.sceneDuos = undefined;
        // }
        this.updateRoomScene();
    };
    @action public revertScene = () => {
        window.clearTimeout(this.advanceTimeoutId);
        this.performanceState.scene -= 1;
        this.performanceState.sceneDuos = undefined;
        this.updateRoomScene();
    };

    private updateRoomScene = () => {
        const { room } = this.props;
        const message: PerformanceSceneMessage = {
            kind: "performanceScene",
            scene: this.performanceState.scene,
        };
        for (const layout of this.roomState.videoLayout) {
            room.sendMessage(message, layout.userId);
        }
    };

    @action
    private joinRoom = () => {
        const { room, reconnect } = this.props;
        if (this.roomState.roomJoined === "left") {
            reconnect();
        } else {
            room.join();
        }
        this.roomState.roomJoined = "joining";
    };
}
